/**
 * Self-test suite for the Web Share Target feature.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ShareExtensionSelfTest.swift` case-for-case
 * where applicable, plus web-specific cases for the SW `fetch`-handler path.
 *
 * THREAT-MODEL-RELEVANT INVARIANTS COVERED
 *
 *   POLICY (pure validation)
 *   - Empty items → `noItemsProvided`.
 *   - More than 10 items → `tooManyItems`.
 *   - Caption beyond 1500 unicode scalars → `captionTooLong`. UTF-16-vs-scalar
 *     counting matters: a string of 1500 multi-scalar emoji MUST be rejected
 *     even though `.length` would report 3000 code units.
 *   - MIME outside the allow-list → `invalidMimeType`.
 *   - Bytes-bearing kind with missing MIME → `invalidMimeType("<missing>")`.
 *   - Plaintext invariant violations → `unsupportedKind`.
 *   - Per-item byte cap (e.g. 50 MB image when image cap is 25 MB) →
 *     `oversizeAttachment`.
 *   - Per-item bytes within per-category cap but total > 50 MB →
 *     `totalPayloadOverLimit`.
 *
 *   STORE (IDB draft-pending)
 *   - `put` then `get` round-trips the draft.
 *   - `delete` removes the record.
 *   - Expired drafts read as `null` and are auto-purged.
 *   - `purgeExpired` deletes only the expired ones.
 *
 *   FETCH HANDLER (pure pipeline + URL builder)
 *   - Valid FormData → `redirected` outcome, draft persisted, redirect URL
 *     contains the draft id.
 *   - Invalid FormData (too many items) → `rejected` outcome, no draft
 *     persisted, redirect URL contains the error kind but NOT the count.
 *   - `text` field that looks like a URL with no `url` field → routed as
 *     `url` item.
 *
 *   STRUCTURAL
 *   - The four source files have no `window` / `document` references at
 *     runtime (Service-Worker-importable).
 *   - The `ShareItem` type has no `remoteURL` / `fetchURL` field (compile-
 *     time invariant; we sample a constructed value and check key set).
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob` in `selftest/index.ts`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  MAX_CAPTION_UNICODE_SCALARS,
  MAX_ITEMS_PER_SHARE,
  MAX_TOTAL_PAYLOAD_BYTES,
  validateSharedPayload,
} from "./ShareTargetPolicy";
import type { ShareItem, SharePayloadError } from "./ShareTargetPayload";
import { unicodeScalarCount } from "./ShareTargetPayload";
import {
  DEFAULT_DRAFT_TTL_MS,
  ShareTargetStore,
  newDraftId,
} from "./ShareTargetStore";
import {
  COMPOSER_PATH,
  materialiseAndProcess,
  parseFormData,
  processShareTargetFormData,
} from "./ShareTargetFetchHandler";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

let dbCounter = 0;
function freshStore(): ShareTargetStore {
  dbCounter += 1;
  return new ShareTargetStore(
    `kaether:share-target:selftest:${dbCounter}:${Date.now()}`,
  );
}

function imageItem(bytes: number, mime = "image/png"): ShareItem {
  return {
    kind: "image",
    mimeType: mime,
    plaintextBytes: new Uint8Array(bytes),
    suggestedFilename: "x.png",
  };
}

function textItem(s: string): ShareItem {
  return { kind: "text", plaintextString: s };
}

function urlItem(s: string): ShareItem {
  return { kind: "url", plaintextString: s };
}

function expectReject(
  name: string,
  items: readonly ShareItem[],
  caption: string | undefined,
  predicate: (e: SharePayloadError) => boolean,
): TestCase {
  const d = validateSharedPayload(items, caption);
  if (d.kind !== "reject") {
    return fail(name, `expected reject, got allow`);
  }
  return predicate(d.error)
    ? pass(name)
    : fail(name, `wrong error: ${JSON.stringify(d.error)}`);
}

function expectAllow(
  name: string,
  items: readonly ShareItem[],
  caption: string | undefined,
): TestCase {
  const d = validateSharedPayload(items, caption);
  return d.kind === "allow"
    ? pass(name)
    : fail(name, `expected allow, got reject: ${JSON.stringify(d.error)}`);
}

// ---------------------------------------------------------------------------
// A. Policy — count + caption caps
// ---------------------------------------------------------------------------

function testEmptyItems(): TestCase {
  return expectReject(
    "policy: empty items → noItemsProvided",
    [],
    undefined,
    (e) => e.kind === "noItemsProvided",
  );
}

function testTooManyItems(): TestCase {
  const items = Array.from({ length: MAX_ITEMS_PER_SHARE + 1 }, () =>
    textItem("x"),
  );
  return expectReject(
    "policy: 11 items → tooManyItems(11)",
    items,
    undefined,
    (e) => e.kind === "tooManyItems" && e.count === 11,
  );
}

function testCaptionTooLongUtf16OkScalarsOverflow(): TestCase {
  // Emoji "🏳️‍🌈" is 4 scalars (U+1F3F3 U+FE0F U+200D U+1F308).
  // Construct a caption with `MAX_CAPTION_UNICODE_SCALARS / 4 + 1` rainbow
  // flags so the total scalar count exceeds the cap. UTF-16 .length would
  // be larger than the scalar count, but the cap is on scalars.
  const flag = "\u{1F3F3}\u{FE0F}\u{200D}\u{1F308}";
  const repeats = Math.floor(MAX_CAPTION_UNICODE_SCALARS / 4) + 1;
  const caption = flag.repeat(repeats);
  const scalars = unicodeScalarCount(caption);
  return expectReject(
    `policy: caption with ${scalars} scalars (>${MAX_CAPTION_UNICODE_SCALARS}) → captionTooLong`,
    [textItem("ok")],
    caption,
    (e) => e.kind === "captionTooLong" && e.scalarCount === scalars,
  );
}

function testCaptionAtCapAllowed(): TestCase {
  // Exactly at cap is allowed. 1500 ASCII chars = 1500 scalars.
  const caption = "a".repeat(MAX_CAPTION_UNICODE_SCALARS);
  return expectAllow(
    "policy: caption exactly at scalar cap → allow",
    [textItem("ok")],
    caption,
  );
}

function testUnicodeScalarCountEmoji(): TestCase {
  // Standalone verification: the rainbow flag is 4 scalars, not 1, not 7
  // (it is 7 UTF-16 code units).
  const flag = "\u{1F3F3}\u{FE0F}\u{200D}\u{1F308}";
  const n = unicodeScalarCount(flag);
  const name = `unicodeScalarCount: rainbow-flag emoji counts as 4 scalars (not ${flag.length})`;
  return n === 4 ? pass(name) : fail(name, `got ${n}`);
}

// ---------------------------------------------------------------------------
// B. Policy — MIME + plaintext invariants
// ---------------------------------------------------------------------------

function testMimeNotInAllowList(): TestCase {
  const item: ShareItem = {
    kind: "file",
    mimeType: "application/x-executable",
    plaintextBytes: new Uint8Array(10),
  };
  return expectReject(
    "policy: MIME outside allow-list → invalidMimeType",
    [item],
    undefined,
    (e) => e.kind === "invalidMimeType" && e.mime === "application/x-executable",
  );
}

function testMissingMimeOnBytesKind(): TestCase {
  const item: ShareItem = {
    kind: "image",
    plaintextBytes: new Uint8Array(10),
  };
  return expectReject(
    "policy: image with missing MIME → invalidMimeType('<missing>')",
    [item],
    undefined,
    (e) => e.kind === "invalidMimeType" && e.mime === "<missing>",
  );
}

function testMissingMimeOnTextKindAllowed(): TestCase {
  return expectAllow(
    "policy: text without MIME → allow",
    [textItem("hello")],
    undefined,
  );
}

function testPlaintextInvariantTextWithBytes(): TestCase {
  const item: ShareItem = {
    kind: "text",
    plaintextString: "hello",
    plaintextBytes: new Uint8Array(1),
  };
  return expectReject(
    "policy: text item with bytes → unsupportedKind",
    [item],
    undefined,
    (e) => e.kind === "unsupportedKind",
  );
}

function testPlaintextInvariantImageWithoutBytes(): TestCase {
  const item: ShareItem = { kind: "image", mimeType: "image/png" };
  return expectReject(
    "policy: image item without bytes → unsupportedKind",
    [item],
    undefined,
    (e) => e.kind === "unsupportedKind",
  );
}

// ---------------------------------------------------------------------------
// C. Policy — byte caps
// ---------------------------------------------------------------------------

function testOversizeImage(): TestCase {
  // Image cap is 25 MB; 26 MB image rejects with oversizeAttachment.
  const big = imageItem(26 * 1024 * 1024);
  return expectReject(
    "policy: 26 MB image > 25 MB image cap → oversizeAttachment",
    [big],
    undefined,
    (e) => e.kind === "oversizeAttachment",
  );
}

function testTotalPayloadOverLimit(): TestCase {
  // Each image is 20 MB (under the 25 MB image cap), 3 of them = 60 MB
  // which is over the 50 MB total cap.
  const items = [
    imageItem(20 * 1024 * 1024),
    imageItem(20 * 1024 * 1024),
    imageItem(20 * 1024 * 1024),
  ];
  return expectReject(
    "policy: 3×20MB images = 60MB > 50MB total → totalPayloadOverLimit",
    items,
    undefined,
    (e) =>
      e.kind === "totalPayloadOverLimit" &&
      e.totalBytes === 60 * 1024 * 1024,
  );
}

function testNormalShareAllowed(): TestCase {
  return expectAllow(
    "policy: 1 image (1 MB) + caption (10 chars) → allow",
    [imageItem(1024 * 1024)],
    "look at this",
  );
}

// ---------------------------------------------------------------------------
// D. Store — round-trip + expiry
// ---------------------------------------------------------------------------

async function testStoreRoundTrip(): Promise<TestCase> {
  const name = "store: put then get round-trips the draft";
  try {
    const store = freshStore();
    const now = Date.now();
    const draft = {
      id: newDraftId(now),
      items: [textItem("hello")],
      captionText: "cap",
      createdAt: now,
      expiresAt: now + DEFAULT_DRAFT_TTL_MS,
    };
    await store.put(draft);
    const out = await store.get(draft.id, now + 1);
    if (!out) return fail(name, "got null from get");
    const ok =
      out.id === draft.id &&
      out.items.length === 1 &&
      out.items[0].plaintextString === "hello" &&
      out.captionText === "cap";
    return ok ? pass(name) : fail(name, `mismatch: ${JSON.stringify(out)}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testStoreDelete(): Promise<TestCase> {
  const name = "store: delete removes the record";
  try {
    const store = freshStore();
    const now = Date.now();
    const draft = {
      id: newDraftId(now),
      items: [textItem("x")],
      createdAt: now,
      expiresAt: now + DEFAULT_DRAFT_TTL_MS,
    };
    await store.put(draft);
    await store.delete(draft.id);
    const out = await store.get(draft.id, now + 1);
    return out === null ? pass(name) : fail(name, `expected null, got value`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testStoreExpiredReadsAsNull(): Promise<TestCase> {
  const name = "store: expired draft reads as null and is auto-purged";
  try {
    const store = freshStore();
    const now = Date.now();
    const draft = {
      id: newDraftId(now),
      items: [textItem("x")],
      createdAt: now - 7200_000,
      expiresAt: now - 3600_000, // 1 hour in the past
    };
    await store.put(draft);
    const out = await store.get(draft.id, now);
    if (out !== null) return fail(name, "expected null on expired read");
    // Auto-purge: a second get with the future clock still returns null.
    const out2 = await store.get(draft.id, now);
    return out2 === null
      ? pass(name)
      : fail(name, "second get returned non-null, auto-purge failed");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testStorePurgeExpired(): Promise<TestCase> {
  const name = "store: purgeExpired deletes only expired drafts";
  try {
    const store = freshStore();
    const now = Date.now();
    const expired = {
      id: newDraftId(now - 1),
      items: [textItem("a")],
      createdAt: now - 7200_000,
      expiresAt: now - 3600_000,
    };
    const fresh = {
      id: newDraftId(now),
      items: [textItem("b")],
      createdAt: now,
      expiresAt: now + 3600_000,
    };
    await store.put(expired);
    await store.put(fresh);
    const n = await store.purgeExpired(now);
    if (n !== 1) return fail(name, `purgeExpired returned ${n}, want 1`);
    const stillFresh = await store.get(fresh.id, now);
    return stillFresh !== null
      ? pass(name)
      : fail(name, "fresh draft was incorrectly purged");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// E. Fetch handler — happy path + reject path + parseFormData edge cases
// ---------------------------------------------------------------------------

function makeForm(parts: {
  title?: string;
  text?: string;
  url?: string;
  files?: { name: string; type: string; bytes: Uint8Array }[];
}): FormData {
  const form = new FormData();
  if (parts.title !== undefined) form.append("title", parts.title);
  if (parts.text !== undefined) form.append("text", parts.text);
  if (parts.url !== undefined) form.append("url", parts.url);
  for (const f of parts.files ?? []) {
    const blob = new Blob([f.bytes], { type: f.type });
    form.append("files", blob, f.name);
  }
  return form;
}

async function testHandlerHappyPath(): Promise<TestCase> {
  const name = "handler: valid 1 image + caption → redirected with draft id";
  try {
    const store = freshStore();
    const form = makeForm({
      title: "look",
      files: [
        {
          name: "x.png",
          type: "image/png",
          bytes: new Uint8Array(1024),
        },
      ],
    });
    const outcome = await materialiseAndProcess(form, store);
    if (outcome.kind !== "redirected") {
      return fail(name, `kind=${outcome.kind}`);
    }
    if (!outcome.redirectUrl.startsWith(`${COMPOSER_PATH}?draft=`)) {
      return fail(name, `bad redirect URL: ${outcome.redirectUrl}`);
    }
    const draft = await store.get(outcome.draftId);
    if (!draft) return fail(name, "draft not persisted");
    if (draft.items.length !== 1) return fail(name, `items=${draft.items.length}`);
    if (draft.items[0].kind !== "image") return fail(name, `kind=${draft.items[0].kind}`);
    if (draft.captionText !== "look") return fail(name, `caption=${draft.captionText}`);
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testHandlerTooManyItemsRedirectsToError(): Promise<TestCase> {
  const name = "handler: 11 items → rejected, no draft persisted, error in URL";
  try {
    const store = freshStore();
    const files = Array.from({ length: 11 }, (_, i) => ({
      name: `x${i}.png`,
      type: "image/png",
      bytes: new Uint8Array(1),
    }));
    const form = makeForm({ files });
    const outcome = await materialiseAndProcess(form, store);
    if (outcome.kind !== "rejected") return fail(name, `kind=${outcome.kind}`);
    if (outcome.error.kind !== "tooManyItems")
      return fail(name, `error=${outcome.error.kind}`);
    if (!outcome.redirectUrl.includes("error=tooManyItems"))
      return fail(name, `bad redirect URL: ${outcome.redirectUrl}`);
    // We do NOT leak the count back to the page URL.
    if (outcome.redirectUrl.includes("11"))
      return fail(name, `redirect URL leaks count: ${outcome.redirectUrl}`);
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

function testParseFormDataTextLooksLikeUrl(): TestCase {
  const name = "parseFormData: text='https://x.example/y' with no url field → url item";
  const form = makeForm({ text: "https://x.example/y" });
  const { items, caption } = parseFormData(form);
  if (items.length !== 1) return fail(name, `items=${items.length}`);
  if (items[0].kind !== "url") return fail(name, `kind=${items[0].kind}`);
  if (caption !== undefined) return fail(name, `caption=${caption}`);
  return pass(name);
}

function testParseFormDataTitleAndText(): TestCase {
  const name =
    "parseFormData: both title and text present → caption is 'title\\ntext'";
  const form = makeForm({ title: "T", text: "hello world" });
  const { caption } = parseFormData(form);
  return caption === "T\nhello world"
    ? pass(name)
    : fail(name, `caption=${JSON.stringify(caption)}`);
}

async function testHandlerNoNetworkSurface(): Promise<TestCase> {
  // Structural: `processShareTargetFormData` source must not reference
  // `fetch(` or `XMLHttpRequest` or `new URL(...).host`-style outbound
  // calls. We verify by running the pipeline and inspecting that the
  // store recorded the bytes from the FormData (not a refetched URL).
  const name = "handler: shared bytes come from FormData, not from a refetch";
  try {
    const store = freshStore();
    const distinctive = new Uint8Array([0xde, 0xad, 0xbe, 0xef, 0x00, 0xff]);
    const form = makeForm({
      files: [{ name: "x.bin", type: "application/octet-stream", bytes: distinctive }],
    });
    const outcome = await materialiseAndProcess(form, store);
    if (outcome.kind !== "redirected") return fail(name, `kind=${outcome.kind}`);
    const draft = await store.get(outcome.draftId);
    if (!draft) return fail(name, "draft not persisted");
    const bytes = draft.items[0].plaintextBytes;
    if (!bytes) return fail(name, "no bytes on draft");
    const ok =
      bytes.length === distinctive.length &&
      bytes.every((b, i) => b === distinctive[i]);
    return ok ? pass(name) : fail(name, `bytes mismatch`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// F. Structural — ShareItem has no remoteURL / fetchURL field
// ---------------------------------------------------------------------------

function testShareItemHasNoRemoteUrl(): TestCase {
  const name = "structural: ShareItem has no remoteURL / fetchURL field";
  // We can't introspect the TypeScript type at runtime, but we can build
  // a value via the allowed shape and verify it never sprouts a remoteURL
  // key — the type system would already reject that at the call site.
  const item: ShareItem = textItem("hi");
  const keys = Object.keys(item);
  const forbidden = ["remoteURL", "fetchURL", "assetIdentifier"];
  for (const k of forbidden) {
    if (keys.includes(k)) {
      return fail(name, `ShareItem unexpectedly has field ${k}`);
    }
  }
  return pass(name);
}

function testNoWindowDocumentRuntime(): TestCase {
  // Pure-function smoke test — exercising every module function in a way
  // that would throw under a SW context if any of them dereferenced
  // `window` or `document`.
  const name = "SW-importable: pure functions run with no DOM access";
  try {
    void validateSharedPayload([textItem("x")], "y");
    void unicodeScalarCount("z");
    void parseFormData(new FormData());
    // processShareTargetFormData is async but importing it is enough; we
    // don't call it without a store here.
    void processShareTargetFormData;
    void newDraftId(0);
    return pass(name);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// G. Sanity — caps are the numbers iOS uses
// ---------------------------------------------------------------------------

function testCapsMatchIos(): TestCase {
  const name = "caps: MAX_ITEMS=10, MAX_TOTAL_BYTES=50MB, MAX_CAPTION_SCALARS=1500";
  const ok =
    MAX_ITEMS_PER_SHARE === 10 &&
    MAX_TOTAL_PAYLOAD_BYTES === 50 * 1024 * 1024 &&
    MAX_CAPTION_UNICODE_SCALARS === 1500;
  return ok
    ? pass(name)
    : fail(
        name,
        `items=${MAX_ITEMS_PER_SHARE} total=${MAX_TOTAL_PAYLOAD_BYTES} caption=${MAX_CAPTION_UNICODE_SCALARS}`,
      );
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Web Share Target (policy + IDB draft + SW fetch)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // A. Policy — count + caption
      testEmptyItems(),
      testTooManyItems(),
      testCaptionTooLongUtf16OkScalarsOverflow(),
      testCaptionAtCapAllowed(),
      testUnicodeScalarCountEmoji(),
      // B. Policy — MIME + plaintext invariants
      testMimeNotInAllowList(),
      testMissingMimeOnBytesKind(),
      testMissingMimeOnTextKindAllowed(),
      testPlaintextInvariantTextWithBytes(),
      testPlaintextInvariantImageWithoutBytes(),
      // C. Policy — byte caps
      testOversizeImage(),
      testTotalPayloadOverLimit(),
      testNormalShareAllowed(),
      // D. Store
      await testStoreRoundTrip(),
      await testStoreDelete(),
      await testStoreExpiredReadsAsNull(),
      await testStorePurgeExpired(),
      // E. Fetch handler
      await testHandlerHappyPath(),
      await testHandlerTooManyItemsRedirectsToError(),
      testParseFormDataTextLooksLikeUrl(),
      testParseFormDataTitleAndText(),
      await testHandlerNoNetworkSurface(),
      // F. Structural
      testShareItemHasNoRemoteUrl(),
      testNoWindowDocumentRuntime(),
      // G. Sanity
      testCapsMatchIos(),
    ];
    // unused-import sentinel: keep `urlItem` reachable for future cases.
    void urlItem;
    return buildSuiteResult(cases);
  },
});
